import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Encabezado } from '../navegacion/Marco';
import { Avatar } from './FichaPaciente';
import {
  Aviso,
  Boton,
  Cajon,
  Campo,
  Cargando,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
} from '../comun/componentes';
import { useAccion, useDatos, useRetraso } from '../comun/useDatos';
import { enviar } from '../api/cliente';
import { useSesion } from '../sesion/Sesion';
import type { PacienteResumen } from '../api/tipos';

const TIPOS_DOCUMENTO = [
  { valor: 'CedulaPy', rotulo: 'Cédula paraguaya' },
  { valor: 'RucPy', rotulo: 'RUC' },
  { valor: 'Pasaporte', rotulo: 'Pasaporte' },
  { valor: 'CpfBr', rotulo: 'CPF (Brasil)' },
  { valor: 'RgBr', rotulo: 'RG (Brasil)' },
  { valor: 'DniAr', rotulo: 'DNI (Argentina)' },
];

/**
 * Listado de pacientes con búsqueda instantánea. La búsqueda es la acción real de esta
 * pantalla —la recepción entra acá cuando el paciente ya está parado en el mostrador— así
 * que el campo tiene el foco y filtra mientras se escribe.
 */
export function Pacientes() {
  const { puede } = useSesion();
  const [texto, setTexto] = useState('');
  const buscado = useRetraso(texto);
  const [alta, setAlta] = useState(false);

  const consulta = buscado.trim() ? `?q=${encodeURIComponent(buscado.trim())}` : '';
  const { datos, cargando, error, recargar } = useDatos<PacienteResumen[]>(
    `/api/v1/pacientes${consulta}`,
  );

  return (
    <>
      <Encabezado
        titulo="Pacientes"
        detalle="Buscá por nombre, apellido o documento."
        accion={
          puede('paciente.crear') && (
            <Boton variante="primario" onClick={() => setAlta(true)}>
              Nuevo paciente
            </Boton>
          )
        }
      />

      <div className="mb-4">
        <label className="sr-only" htmlFor="buscador">
          Buscar paciente
        </label>
        <input
          id="buscador"
          type="search"
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Nombre, apellido o número de documento…"
          className="campo text-base"
        />
      </div>

      <Panel>
        {cargando && <Cargando que="los pacientes" />}
        {error && <ErrorEnPantalla mensaje={error} reintentar={recargar} />}

        {datos && datos.length === 0 && (
          <Vacio
            titulo={buscado ? 'Ningún paciente coincide' : 'Todavía no hay pacientes'}
            detalle={
              buscado
                ? 'Probá con el apellido, o con el número de documento sin puntos.'
                : 'Dá de alta al primero y su ficha queda lista para agendarle una cita.'
            }
            accion={
              puede('paciente.crear') && (
                <Boton variante="primario" onClick={() => setAlta(true)}>
                  Nuevo paciente
                </Boton>
              )
            }
          />
        )}

        {datos && datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <caption className="sr-only">Pacientes de la clínica</caption>
              <thead>
                <tr>
                  <th scope="col">Paciente</th>
                  <th scope="col">Documento</th>
                  <th scope="col">Teléfono</th>
                  <th scope="col">Edad</th>
                </tr>
              </thead>
              <tbody>
                {datos.map((paciente) => (
                  <tr key={paciente.id} className="hover:bg-papel-50">
                    <td>
                      <Link
                        to={`/pacientes/${paciente.id}`}
                        className="flex items-center gap-3 font-medium text-tinta-900 hover:text-marca-700"
                      >
                        <Avatar nombre={paciente.nombreCompleto} />
                        <span className="min-w-0">
                          <span className="block truncate">{paciente.nombreCompleto}</span>
                          {paciente.esMenor && (
                            <Insignia tono="aviso" className="mt-0.5">
                              Menor
                            </Insignia>
                          )}
                        </span>
                      </Link>
                    </td>
                    <td className="cifra whitespace-nowrap">{paciente.documento ?? '—'}</td>
                    <td className="cifra whitespace-nowrap">{paciente.telefono ?? '—'}</td>
                    <td className="cifra">{paciente.edad ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <AltaDePaciente
        abierto={alta}
        alCerrar={() => setAlta(false)}
        alGuardar={() => {
          setAlta(false);
          recargar();
        }}
      />
    </>
  );
}

function AltaDePaciente({
  abierto,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const { correr, ejecutando, error, porCampo } = useAccion();
  const [nombres, setNombres] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [tipo, setTipo] = useState('CedulaPy');
  const [documento, setDocumento] = useState('');
  const [telefono, setTelefono] = useState('');
  const [nacimiento, setNacimiento] = useState('');

  const guardar = async () => {
    const creado = await correr(() =>
      enviar<{ id: string }>('/api/v1/pacientes', {
        nombres: nombres.trim(),
        apellidos: apellidos.trim(),
        fechaNacimiento: nacimiento || null,
        telefonoPrincipal: telefono.trim() || null,
        documentos: documento.trim()
          ? [{ tipo, numero: documento.trim(), esPrincipal: true }]
          : [],
      }),
    );

    if (creado) {
      setNombres('');
      setApellidos('');
      setDocumento('');
      setTelefono('');
      setNacimiento('');
      alGuardar();
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Nuevo paciente"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void guardar()}>
            Guardar paciente
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-dato text-tinta-500">
          Con el nombre y el documento alcanza para empezar. El resto se completa en la ficha.
        </p>

        <Campo
          etiqueta="Nombres"
          required
          value={nombres}
          error={porCampo.Nombres?.[0]}
          onChange={(e) => setNombres(e.target.value)}
        />
        <Campo
          etiqueta="Apellidos"
          required
          value={apellidos}
          error={porCampo.Apellidos?.[0]}
          onChange={(e) => setApellidos(e.target.value)}
        />

        <div className="grid gap-3 tablet:grid-cols-2">
          <Selector etiqueta="Tipo de documento" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS_DOCUMENTO.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.rotulo}
              </option>
            ))}
          </Selector>
          <Campo
            etiqueta="Número"
            inputMode="numeric"
            value={documento}
            error={porCampo.Documentos?.[0]}
            ayuda="Sin puntos ni guiones."
            onChange={(e) => setDocumento(e.target.value)}
          />
        </div>

        <div className="grid gap-3 tablet:grid-cols-2">
          <Campo
            etiqueta="Teléfono"
            inputMode="tel"
            placeholder="0981 123 456"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
          />
          <Campo
            etiqueta="Fecha de nacimiento"
            type="date"
            value={nacimiento}
            error={porCampo.FechaNacimiento?.[0]}
            onChange={(e) => setNacimiento(e.target.value)}
          />
        </div>

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}
